import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { getBillingConfig } from '../../services/billingService';

function getStoredAuthToken() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('auth_token');
}

const StripeConfigContext = createContext({
  ready: false,
  error: null,
  publishableKey: null,
});

export function useStripeConfig() {
  return useContext(StripeConfigContext);
}

const VolksignStripeProvider = ({ children }) => {
  const authToken = useSelector((state) => state.auth?.token);
  const envPublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null;
  const [publishableKey, setPublishableKey] = useState(envPublishableKey);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const sessionToken = authToken || getStoredAuthToken();

  useEffect(() => {
    if (!sessionToken) {
      setLoading(false);
      setError(null);
      setPublishableKey(envPublishableKey);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const config = await getBillingConfig();
        if (cancelled) return;
        const key =
          config?.publishable_key
          ?? config?.publishableKey
          ?? envPublishableKey
          ?? null;
        if (!key) {
          setError('Stripe publishable key is not configured.');
        } else {
          setPublishableKey(key);
        }
      } catch (err) {
        if (!cancelled) {
          if (envPublishableKey) {
            setPublishableKey(envPublishableKey);
            setError(null);
          } else {
            setError(
              err?.response?.data?.message
              || err?.message
              || 'Could not load billing configuration.'
            );
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionToken, envPublishableKey]);

  const configValue = useMemo(
    () => ({
      ready: !loading && Boolean(publishableKey),
      error,
      publishableKey,
    }),
    [loading, publishableKey, error]
  );

  return (
    <StripeConfigContext.Provider value={configValue}>
      {children}
    </StripeConfigContext.Provider>
  );
};

/**
 * Wrap Payment Element flows with Stripe Elements + client secret.
 */
export function StripeElementsWrapper({ clientSecret, children }) {
  const { publishableKey } = useStripeConfig();
  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey]
  );

  if (!clientSecret || !stripePromise) {
    return null;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            borderRadius: '6px',
          },
        },
        paymentMethodOrder: ['card'],
        wallets: {
          applePay: 'never',
          googlePay: 'never',
        },
      }}
    >
      {children}
    </Elements>
  );
}

export default VolksignStripeProvider;
